import React, { createContext, useContext, useEffect, useState } from 'react';

export type Domain = 'askjds' | 'jdsimplified' | 'admin' | 'studicata' | 'school';

interface DomainContextType {
  currentDomain: Domain;
  isJDSimplified: boolean;
  isAskJDS: boolean;
  isAdmin: boolean;
  isStudicata: boolean;
  isSchool: boolean;
}

const DomainContext = createContext<DomainContextType | undefined>(undefined);

export function DomainProvider({ children }: { children: React.ReactNode }) {
  // Get the initial domain - CRITICAL: Check hostname FIRST to avoid localStorage stale data
  const getInitialDomain = (): Domain => {
    const envMode = import.meta.env.MODE;

    // Environment variable takes precedence over everything else
    if (envMode === 'jds') {
      return 'jdsimplified';
    } else if (envMode === 'askjds') {
      return 'askjds';
    } else if (envMode === 'admin') {
      return 'admin';
    } else if (envMode === 'studicata') {
      return 'studicata';
    } else if (envMode === 'school') {
      return 'school';
    }

    // Check hostname FIRST (don't trust localStorage - it may be stale from previous domain visit)
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const pathname = window.location.pathname;

      // Check if we're on localhost
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // Use path for local development
        if (pathname.startsWith('/admin')) {
          return 'admin';
        } else if (pathname.startsWith('/jds')) {
          return 'jdsimplified';
        }
        // For localhost, fallback to localStorage or default
        const storedDomain = localStorage.getItem('current_domain');
        if (storedDomain === 'jdsimplified' || storedDomain === 'askjds' || storedDomain === 'admin' || storedDomain === 'studicata' || storedDomain === 'school') {
          return storedDomain as Domain;
        }
        return 'askjds';
      } else {
        // Use hostname for production - THIS MUST WORK IMMEDIATELY
        // IMPORTANT: Check more specific subdomains FIRST before general jdsimplified.com
        if (hostname.includes('school.jdsimplified.com')) {
          return 'school';
        } else if (hostname.includes('studicata.jdsimplified.com')) {
          return 'studicata';
        } else if (hostname.includes('admin.jdsimplified.com') || hostname.includes(import.meta.env.VITE_ADMIN_DOMAIN || 'admin.jdsimplified.com')) {
          return 'admin';
        } else if (hostname.includes('dev.jdsimplified.com')) {
          return 'askjds';
        } else if (hostname.includes('jdsimplified.com') || hostname.includes(import.meta.env.VITE_JDSIMPLIFIED_DOMAIN || 'jdsimplified.com')) {
          return 'jdsimplified';
        }
      }
    }

    return 'askjds'; // Default fallback
  };

  const [currentDomain, setCurrentDomain] = useState<Domain>(getInitialDomain());

  useEffect(() => {
    // Check for environment variable first (for npm run dev:jds/askjds/admin)
    const envMode = import.meta.env.MODE;

    let detectedDomain: Domain;

    // Environment variables always take precedence
    if (envMode === 'jds') {
      detectedDomain = 'jdsimplified';
    } else if (envMode === 'askjds') {
      detectedDomain = 'askjds';
    } else if (envMode === 'admin') {
      detectedDomain = 'admin';
    } else if (envMode === 'studicata') {
      detectedDomain = 'studicata';
    } else if (envMode === 'school') {
      detectedDomain = 'school';
    } else {
      // If no environment variable, check hostname
      const hostname = window.location.hostname;
      
      // Check if we're on localhost
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        // Use path for local development
        const path = window.location.pathname;
        if (path.startsWith('/admin')) {
          detectedDomain = 'admin';
        } else if (path.startsWith('/jds')) {
          detectedDomain = 'jdsimplified';
        } else {
          detectedDomain = 'askjds';
        }
      } else {
        // Use hostname for production
        // IMPORTANT: Check more specific subdomains FIRST before general jdsimplified.com
        if (hostname.includes('school.jdsimplified.com')) {
          detectedDomain = 'school';
        } else if (hostname.includes('studicata.jdsimplified.com')) {
          detectedDomain = 'studicata';
        } else if (hostname.includes('admin.jdsimplified.com') || hostname.includes(import.meta.env.VITE_ADMIN_DOMAIN || 'admin.jdsimplified.com')) {
          detectedDomain = 'admin';
        } else if (hostname.includes('dev.jdsimplified.com')) {
          detectedDomain = 'askjds';
        } else if (hostname.includes('jdsimplified.com') || hostname.includes(import.meta.env.VITE_JDSIMPLIFIED_DOMAIN || 'jdsimplified.com')) {
          detectedDomain = 'jdsimplified';
        } else {
          detectedDomain = 'askjds';
        }
      }
    }
    
    // Save to localStorage to prevent flashing between domains on future loads
    localStorage.setItem('current_domain', detectedDomain);
    setCurrentDomain(detectedDomain);
  }, []);

  const value = {
    currentDomain,
    isJDSimplified: currentDomain === 'jdsimplified',
    isAskJDS: currentDomain === 'askjds',
    isAdmin: currentDomain === 'admin',
    isStudicata: currentDomain === 'studicata',
    isSchool: currentDomain === 'school',
  };

  return (
    <DomainContext value={value}>
      {children}
    </DomainContext>
  );
}

export function useDomain() {
  const context = useContext(DomainContext);
  if (context === undefined) {
    throw new Error('useDomain must be used within a DomainProvider');
  }
  return context;
} 